import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import cx from 'classnames';

import { fitSectionBounds, flyTo } from '../../utils/map';
import { MapContext } from '../../contexts/MapContext';
import { mapModes } from '../../constants/map';
import { ItemContext } from '../../contexts/ItemContext';
import RowIcon from '../icons/RowIcon';
import { PanelSection } from '../SidePanel';
import ChevronIcon from '../icons/ChevronIcon';
import {
  HiddenButton,
  HoverNavItem,
  IconSpan,
  MaxWidthNameInput,
  StickyHeader,
} from './menu-components';
import { getRowId } from '../../utils/field';
import SectionsIcon from '../icons/SectionsIcon';
import { UnstyledButton } from '../basics/UnstyledButton';
import { getIconForSectionType, sectionHasGeodata } from '../../utils/sections';
import { MultiSelectSectionCheckbox } from './MultiSelectCheckbox';
import IconButton from '../basics/IconButton';
import ShowHideSelectedSectionsButton from '../map-toolbars/buttons/ShowHideSelectedSectionsButton';
import { colorForCrop, cropThemes } from '../../utils/colors';
import ColoredSpan from '../basics/ColoredSpan';
import useMultiselectClickHandler from '../../hooks/useMultiselectClickHandler';
import { getCropId } from '../../utils/meta';
import { captureEvent } from '../../utils/analytics';
import {
  alphanumericSort,
  preventShiftClickTextSelection,
} from '../../utils/utils';
import {
  cropNameForRow,
  patternIdForRow,
} from '../../utils/points-and-rows/rows';

export const SectionItem = ({
  actionsEnabled = false,
  checkboxEnabled = false,
  filterRows = () => true, // function of two args: (row, rowIndex); default is to keep all rows
  menuHovered,
  section,
}) => {
  const { name, type, id, fieldFeature, rows, rowOverrides, shown } = section;
  const {
    crops: { availableCrops, cropNameToId },
    fields: { toggleSectionShown, updateFieldSection },
    sectionGroups: { getSectionGroup },
    patterns: { inRowPatternsMap },
    mapState: {
      setMapMode,
      isActiveField,
      activeRowIndexes,
      selectRows,
      mapRef,
    },
  } = useContext(MapContext);

  const { setActiveKey, clearActiveKey, activeKey } = useContext(ItemContext);
  const [rowsOpen, setRowsOpen] = useState(false);

  const processClick = useMultiselectClickHandler();

  const onArrowClick = (e) => {
    e.stopPropagation();
    setRowsOpen(!rowsOpen);
  };

  const onNavClick = (e) => {
    if ((e.metaKey || e.ctrlKey) && actionsEnabled) {
      setMapMode(mapModes.MULTI_SECTION_SELECT, id, {
        selectActiveField: true,
      });
    } else {
      fitSectionBounds({ type, rows, fieldFeature }, mapRef);
      setMapMode(mapModes.SECTION_ENGAGED, id);
    }
  };

  const sectionIsActive = isActiveField({ id });
  const topLevelSectionIsActive = useMemo(
    () =>
      // Show the light blue selected state if a section group is hovered containing
      // this section or if the section is the active section and no row is active.
      (sectionIsActive && activeRowIndexes.length === 0) ||
      getSectionGroup(activeKey)?.sections.some((s) => s.id === id),
    [activeKey, activeRowIndexes, getSectionGroup, id, sectionIsActive],
  );

  const sectionRef = useRef(null);
  useEffect(() => {
    if (sectionIsActive && sectionRef.current) {
      sectionRef.current.scrollIntoView({ block: 'center' });
    }
  }, [sectionIsActive]);

  const onToggleClick = () => {
    toggleSectionShown(id);
  };

  const filteredRowsWithIndex = useMemo(
    () =>
      (rows?.features || [])
        .map((row, i) => [row, i]) // Preserve original row index
        .filter(([row, i]) => filterRows(row, i)),
    [filterRows, rows],
  );

  const rowsByCropOrPatternId = useMemo(
    () =>
      filteredRowsWithIndex.reduce((acc, [, i]) => {
        const assignmentId =
          patternIdForRow(rowOverrides[i], section, {
            inRowPatternsMap,
          }) ||
          getCropId(
            { name: cropNameForRow(rowOverrides[i], section) },
            cropNameToId,
          );
        if (!acc[assignmentId]) acc[assignmentId] = [];
        acc[assignmentId].push(i);

        return acc;
      }, {}) ?? {},
    [
      filteredRowsWithIndex,
      rowOverrides,
      section,
      cropNameToId,
      inRowPatternsMap,
    ],
  );

  return (
    <>
      <HoverNavItem
        onClick={onNavClick}
        isActive={topLevelSectionIsActive}
        onMouseEnter={() => setActiveKey(id)}
        onMouseLeave={clearActiveKey}
        hovered={activeKey === id}
        ref={sectionRef}
      >
        <IconSpan width="11px">
          {!!filteredRowsWithIndex.length && (
            <UnstyledButton onClick={onArrowClick}>
              <ChevronIcon
                direction={rowsOpen ? 'down' : 'right'}
                className="w-100"
              />
            </UnstyledButton>
          )}
        </IconSpan>
        <IconSpan className="d-flex justify-content-center">
          <MultiSelectSectionCheckbox
            id={id}
            isHovered={menuHovered && activeKey === id}
            checkboxEnabled={checkboxEnabled}
            renderDefault={() => getIconForSectionType(type)}
            onSelected={() =>
              fitSectionBounds({ type, rows, fieldFeature }, mapRef)
            }
          />
        </IconSpan>
        <MaxWidthNameInput
          update={(value) => updateFieldSection(id, { name: value })}
          name={name}
          renderDefault={() => (
            <span className={shown ? '' : 'text-muted'}>{name}</span>
          )}
          maxWidth="135px"
        />
        {actionsEnabled && (
          <HiddenButton
            className="mx-3 font-small text-muted align-self-end"
            onClick={(e) => {
              e.stopPropagation();
              onToggleClick();
            }}
          >
            {shown ? 'hide' : 'show'}
          </HiddenButton>
        )}
      </HoverNavItem>
      {rowsOpen &&
        filteredRowsWithIndex.map(([row, i]) => {
          const rowId = getRowId(id, i);
          const { cropName } = row.properties;
          const cropId = getCropId({ name: cropName }, cropNameToId);
          const color = colorForCrop(availableCrops[cropId], cropThemes.DARK);
          const patternId = patternIdForRow(rowOverrides[i], section);
          const assignmentType = patternId ? 'pattern' : 'crop';

          const rowsForCropOrPattern =
            rowsByCropOrPatternId[patternId || cropId];
          const allCropOrPatternRowsSelected =
            rowsForCropOrPattern &&
            rowsForCropOrPattern.every((rowIdx) =>
              activeRowIndexes.includes(rowIdx),
            );

          // If new click isn't in the currently active section, the section is changing.
          const sectionIsChanging = !sectionIsActive;

          const currentSelection = sectionIsChanging ? [] : activeRowIndexes;

          const onRowClick = (evt) => {
            flyTo(row, mapRef);
            clearActiveKey();

            if (evt.metaKey || evt.ctrlKey || evt.shiftKey) {
              const clickType = [
                evt.ctrlKey && 'ctrl',
                evt.metaKey && 'command',
                evt.shiftKey && 'shift',
                'click',
              ]
                .filter((x) => x)
                .join('-');

              captureEvent('multiselect-row-click', { clickType });
            }

            const clickResult = processClick({
              hasShiftKey: evt.shiftKey,
              hasMetaKey: evt.metaKey || evt.ctrlKey,
              index: i,
              currentSelection,
              // Don't persist shift-click state when section is changing:
              resetPreviousClick: sectionIsChanging,
            });

            selectRows({ sectionId: id, rowIndexes: clickResult });
          };

          const handleSelectAllCropClick = (evt) => {
            // Don't trigger the click handler for this row:
            evt.stopPropagation();

            captureEvent('multiselect-rows-with-crop-click', {
              type: allCropOrPatternRowsSelected ? 'unselect' : 'select',
            });

            if (allCropOrPatternRowsSelected) {
              // Unselect all rows with this crop or pattern
              const newRows = currentSelection.filter(
                (rowIdx) => !rowsForCropOrPattern.includes(rowIdx),
              );
              selectRows({ sectionId: id, rowIndexes: newRows });
            } else {
              // Otherwise, select all rows with this crop or pattern
              //  (merging w/ existing selection)
              const newRows = _.uniq([
                ...currentSelection,
                ...rowsForCropOrPattern,
              ]);
              selectRows({ sectionId: id, rowIndexes: newRows });
            }
          };

          const hasRowOverride = !!rowOverrides[i];
          const iconColor = actionsEnabled
            ? color
            : activeKey === rowId
            ? 'var(--text-color)'
            : 'var(--text-muted-color)';
          const textColor = !actionsEnabled
            ? 'var(--text-color)'
            : hasRowOverride
            ? color
            : 'var(--text-muted-color)';

          return (
            <HoverNavItem
              key={rowId}
              onClick={onRowClick}
              onMouseDown={preventShiftClickTextSelection}
              onMouseEnter={() => setActiveKey(rowId)}
              onMouseLeave={clearActiveKey}
              hovered={activeKey === rowId}
              isActive={sectionIsActive && activeRowIndexes.includes(i)}
            >
              <ColoredSpan color={iconColor}>
                <IconSpan className="ml-4 d-flex justify-content-center">
                  <RowIcon />
                </IconSpan>
              </ColoredSpan>
              <ColoredSpan
                color={textColor}
                className={cx('text-nowrap', {
                  'font-weight-bold': actionsEnabled && hasRowOverride,
                })}
              >
                Row {i + 1}
              </ColoredSpan>
              {actionsEnabled && (
                <HiddenButton
                  className="pl-1 ml-auto font-small text-muted text-truncate"
                  onClick={handleSelectAllCropClick}
                >
                  {`${
                    allCropOrPatternRowsSelected ? 'unselect' : 'select'
                  } rows with ${assignmentType}`}
                </HiddenButton>
              )}
            </HoverNavItem>
          );
        })}
    </>
  );
};

const FieldsTree = () => {
  const {
    fields: { fieldSections },
    mapState: { mapMode, selectedFieldIds, activeSectionGroupId },
  } = useContext(MapContext);

  const [menuHovered, setMenuHovered] = useState(false);
  const [allSectionsShown, setAllSectionsShown] = useState(true);

  useEffect(() => {
    setAllSectionsShown(mapMode !== mapModes.SECTION_GROUP_ENGAGED);
  }, [activeSectionGroupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredFieldSections = useMemo(
    () =>
      [...fieldSections]
        .sort((a, b) => alphanumericSort(a.name, b.name))
        .filter(({ id }) => allSectionsShown || selectedFieldIds.has(id)),
    [allSectionsShown, fieldSections, selectedFieldIds],
  );

  if (!filteredFieldSections.length) return null;

  return (
    <PanelSection
      onMouseEnter={() => setMenuHovered(true)}
      onMouseLeave={() => setMenuHovered(false)}
    >
      <StickyHeader className="justify-content-between">
        <span className="d-flex align-items-center">
          <IconSpan>
            <SectionsIcon />
          </IconSpan>
          Sections
        </span>
        <ShowHideSelectedSectionsButton
          selectedSectionIdSet={selectedFieldIds}
        />
      </StickyHeader>
      <ul>
        {filteredFieldSections.map((section) => (
          <SectionItem
            key={section.id}
            actionsEnabled
            checkboxEnabled={allSectionsShown && sectionHasGeodata(section)}
            menuHovered={menuHovered}
            section={section}
          />
        ))}
      </ul>
      {mapMode === mapModes.SECTION_GROUP_ENGAGED && (
        <div className="d-flex justify-content-end">
          <IconButton
            onClick={() => setAllSectionsShown(!allSectionsShown)}
            className="font-small mt-1 p-0"
            color="link"
          >
            {allSectionsShown ? ' Selected' : ' All'} sections
            <ChevronIcon direction={allSectionsShown ? 'up' : 'down'} />
          </IconButton>
        </div>
      )}
    </PanelSection>
  );
};

export default FieldsTree;
